import type { AxiosError, AxiosInstance, AxiosStatic } from "axios";
import type { AxiosService } from "@/interfaces/axiosServiceInterface";

export const axiosService = (axiosInst: AxiosStatic): AxiosService => {
  const BASE_URL = import.meta.env.VITE_API_BASEURL;

  const axiosInstance: AxiosInstance = axiosInst.create({
    baseURL: BASE_URL,
    withCredentials: false,
    headers: {
      "Content-Type": "application/json",
    },
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      // Any status code from range of 2xx
      // Do something with response data
      return response;
    },
    async (error: AxiosError) => {
      // Any status codes outside range of 2xx
      // if error es 401 Unauthorized and exist refresh_token on localStorage refresh the auth token
      if (error.response && error.config) {
        if (error.response.status === 401) {
          const refresh_token = localStorage.refresh_token;
          if (!refresh_token || error.config?.url === "/auth/refresh-token") {
            console.log("YAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA");
            localStorage.clear();
            location.href = "/";
          } else {
            try {
              await axiosInstance.post("/auth/refresh-token", {
                refresh_token,
              });
              return await axiosInstance.request(error.config);
            } catch (error) {
              return Promise.reject(error);
            }
          }
        }
      }
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    function (response: any) {
      //verify if response data contains  access_token or refresh_token
      const { access_token, refresh_token } = response.data;
      // if contain access_token store it in localStorage
      if (access_token) {
        localStorage.setItem("access_token", access_token);
      }

      // if contain refresh_token store it in localStorage
      if (refresh_token) {
        localStorage.setItem("refresh_token", refresh_token);
      }
      // return the data of the response
      return response.data;
    },

    function (error: any) {
      throw error;
    }
  );

  axiosInstance.interceptors.request.use(
    async function (config: any) {
      // read if exist access_token on localStorage
      const access_token = localStorage.getItem("access_token");

      // if exist access_token include on request headers for authentication
      if (access_token) {
        config.headers.Authorization = `Bearer ${access_token}`;
      }

      return config;
    },
    function (error: any) {
      throw error;
    }
  );

  // this is defined this way because if axios is deprecated then another
  // library will be implemented and keep the rest of the code the same
  return {
    get<T = any, P = any>(url: string, params?: P): Promise<T> {
      return axiosInstance.get(url, { params });
    },

    getBlob<T = any, P = any>(url: string, params?: P): Promise<T> {
      return axiosInstance.get(url, { params, responseType: "blob" });
    },

    post<T = any, P = any>(
      url: string,
      data?: P | object,
      multipart?: boolean
    ): Promise<any> {
      if (multipart === true) {
        return axiosInstance.post(url, data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        return axiosInstance.post(url, data, {
          headers: {
            "Content-Type": "application/json",
          },
        });
      }
    },

    postWithParams<T = any, P = any, D = any>(
      url: string,
      data: D | object,
      params: P,
      multipart?: boolean
    ): Promise<T> {
      if (multipart === true) {
        return axiosInstance.post(url, data, {
          params,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        return axiosInstance.post(url, data, {
          params,
          headers: {
            "Content-Type": "application/json",
          },
        });
      }
    },

    patch<T = any, P = any, D = any>(
      url: string,
      params?: P,
      data?: D | object,
      multipart?: boolean
    ): Promise<T> {
      if (multipart === true) {
        return axiosInstance.patch(url, data, {
          params,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        return axiosInstance.patch(url, data, {
          params,
          headers: {
            "Content-Type": "application/json",
          },
        });
      }
      //return axiosInstance.patch(url, data);
    },

    delete<T = any>(url: string): Promise<T> {
      return axiosInstance.delete(url);
    },

    upload: (url: string, file: FormData | File): Promise<any> =>
      axiosInstance.post(url, file, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
  };
};
